/* Ads admin panel — single-image responsive ads with weighted rotation,
 * flight dates and a global rotation mode (random vs sequential).
 *
 * Per-ad fields:
 *   • image_url     — one image or short looping video; the AdBanner front-end
 *                     auto-resizes it to fit any viewport.
 *   • target_url    — click-through.
 *   • title         — accessible label / text-only fallback.
 *   • weight        — 1-100 relative serving weight (heavier = more impressions).
 *   • always_on     — when true, the ad serves indefinitely.
 *   • flight_start, flight_end — ISO dates (YYYY-MM-DD); the ad only serves
 *                     between these dates inclusive. Both required when not
 *                     always-on.
 *
 * Global rotation mode (saved to site settings):
 *   • "random"      — weighted random pick per request (heavier weights more often)
 *   • "sequential"  — cycle through every active in-flight ad, weight-aware
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Trash, EyeSlash, Eye, UploadSimple, PencilSimple, X as XIcon, Scissors } from "@phosphor-icons/react";
import Media from "@/components/Media";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MAX_UPLOAD_MB = 10;
const ACCEPT_MEDIA = "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime";

function uploadFile(file, setBusy) {
  return (async () => {
    if (!file) return null;
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      toast.error(`File is too large — please keep uploads under ${MAX_UPLOAD_MB} MB.`);
      return null;
    }
    setBusy?.(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      return data.url;
    } catch { toast.error("Upload failed"); return null; }
    finally { setBusy?.(false); }
  })();
}

function ImageInput({ value, onChange, busy, setBusy, testidPrefix }) {
  const ref = useRef(null);
  return (
    <div className="bg-rf-cream-alt rounded-xl p-3" data-testid={`${testidPrefix}-image`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div>
          <p className="text-xs font-black uppercase tracking-wider text-rf-ink">Ad creative</p>
          <p className="text-[10px] text-rf-ink-soft">One image or short looping video — auto-resized to fit every viewport.</p>
        </div>
        <div className="flex items-center gap-2">
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-rf-ink-soft hover:text-red-600 p-1 rounded"
              title="Clear image"
            ><XIcon size={14} /></button>
          )}
          <button
            type="button"
            onClick={() => ref.current?.click()}
            disabled={busy}
            data-testid={`${testidPrefix}-upload`}
            className="btn-outline-ink !py-1.5 !px-3 !text-xs"
          ><UploadSimple size={12} /> {busy ? "…" : (value ? "Replace" : "Upload")}</button>
          <input ref={ref} type="file" accept={ACCEPT_MEDIA} className="hidden" onChange={async (e) => {
            const url = await uploadFile(e.target.files?.[0], setBusy);
            if (url) onChange(url);
          }} />
        </div>
      </div>
      <div className="rounded-md overflow-hidden border border-rf-ink/10 bg-white flex items-center justify-center min-h-[80px]">
        {value
          ? <Media src={value} alt="" className="block w-full h-auto max-h-48 object-contain" />
          : <span className="text-[10px] text-rf-ink-soft px-2 text-center py-6">No image yet — upload one to see preview.</span>}
      </div>
    </div>
  );
}

function ScheduleFields({ ad, onChange, testidPrefix }) {
  const setF = (k, v) => onChange({ ...ad, [k]: v });
  return (
    <div className="grid sm:grid-cols-4 gap-3">
      <div>
        <label className="overline text-rf-ink">Weight % (1-100)</label>
        <input
          type="number"
          min={1}
          max={100}
          value={ad.weight ?? 10}
          onChange={(e) => setF("weight", Math.max(1, Math.min(100, parseInt(e.target.value || "10", 10))))}
          data-testid={`${testidPrefix}-weight`}
          className="input-rf mt-1"
        />
        <p className="text-[10px] text-rf-ink-soft mt-1">Relative serve-share across all live ads.</p>
      </div>
      <div className="flex flex-col">
        <label className="overline text-rf-ink">Always on</label>
        <label className="inline-flex items-center gap-2 mt-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={!!ad.always_on}
            onChange={(e) => setF("always_on", e.target.checked)}
            data-testid={`${testidPrefix}-always-on`}
            className="w-4 h-4 accent-rf-orange"
          />
          <span className="text-rf-ink-soft">Run with no end date</span>
        </label>
      </div>
      <div>
        <label className="overline text-rf-ink">Flight start</label>
        <input
          type="date"
          value={ad.flight_start || ""}
          disabled={!!ad.always_on}
          onChange={(e) => setF("flight_start", e.target.value || null)}
          data-testid={`${testidPrefix}-flight-start`}
          className="input-rf mt-1 disabled:opacity-40"
        />
      </div>
      <div>
        <label className="overline text-rf-ink">Flight end</label>
        <input
          type="date"
          value={ad.flight_end || ""}
          disabled={!!ad.always_on}
          onChange={(e) => setF("flight_end", e.target.value || null)}
          data-testid={`${testidPrefix}-flight-end`}
          className="input-rf mt-1 disabled:opacity-40"
        />
      </div>
    </div>
  );
}

function RotationModeBar({ mode, onChange }) {
  return (
    <div className="card-recipe mb-4 flex items-center gap-4 flex-wrap" data-testid="ad-rotation-bar">
      <div>
        <p className="overline mb-1">Rotation mode</p>
        <p className="text-xs text-rf-ink-soft max-w-md">
          <b>Random</b> picks an ad on each page-view, weighted by your % share.
          <b className="ml-1">Sequential</b> cycles through every live ad in order, still respecting the weights.
        </p>
      </div>
      <div className="flex gap-2 ml-auto">
        <button
          data-testid="rotation-mode-random"
          onClick={() => onChange("random")}
          className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${mode === "random" ? "bg-rf-amber text-rf-ink" : "bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-amber/30"}`}
        >Random (weighted)</button>
        <button
          data-testid="rotation-mode-sequential"
          onClick={() => onChange("sequential")}
          className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${mode === "sequential" ? "bg-rf-amber text-rf-ink" : "bg-rf-cream-alt text-rf-ink-soft hover:bg-rf-amber/30"}`}
        >Sequential (rotation)</button>
      </div>
    </div>
  );
}

function ImageHelpersBar() {
  return (
    <div className="mb-6 flex items-center gap-2 flex-wrap text-xs text-rf-ink-soft">
      <span className="font-bold uppercase tracking-wider">Tip:</span>
      <span>Need to crop a creative to a specific aspect before uploading?</span>
      <a
        href="https://retoucher.online/image-cropper"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="ad-image-cropper-link"
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-rf-amber/30 hover:bg-rf-amber text-rf-ink font-bold transition-colors"
      >
        <Scissors size={12} weight="bold" /> Open Image Cropper ↗
      </a>
    </div>
  );
}

// CTR badge colour thresholds — quick health signal for each creative.
function ctrBadgeColour(ctrPct, impressions) {
  if (impressions < 50) return "bg-rf-cream-alt text-rf-ink-soft";  // not enough data yet
  if (ctrPct >= 2)   return "bg-green-100 text-green-800";
  if (ctrPct >= 0.5) return "bg-amber-100 text-amber-800";
  return "bg-red-100 text-red-700";
}

const BLANK_AD = {
  image_url: "",
  target_url: "",
  title: "",
  weight: 10,
  always_on: true,
  flight_start: null,
  flight_end: null,
};

export default function AdsPanel() {
  const [ads, setAds] = useState([]);
  const [newAd, setNewAd] = useState(BLANK_AD);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [rotationMode, setRotationMode] = useState("random");

  const refresh = () => api.get("/ads").then((r) => setAds(r.data));
  const refreshSettings = () => api.get("/settings").then((r) => setRotationMode(r.data?.ad_rotation_mode || "random"));

  useEffect(() => {
    refresh();
    refreshSettings();
  }, []);

  const saveRotationMode = async (mode) => {
    setRotationMode(mode);
    try {
      await api.patch("/settings", { ad_rotation_mode: mode });
      toast.success(`Rotation: ${mode === "sequential" ? "Sequential" : "Random"}`);
    } catch { toast.error("Could not save rotation mode"); }
  };

  const validateSchedule = (ad) => {
    if (ad.always_on) return true;
    if (!ad.flight_start || !ad.flight_end) {
      toast.error("Set both flight start and end dates, or tick 'Always on'.");
      return false;
    }
    if (ad.flight_end < ad.flight_start) {
      toast.error("Flight end must be on or after flight start.");
      return false;
    }
    return true;
  };

  const createAd = async () => {
    if (!newAd.image_url || !newAd.target_url) {
      toast.error("Upload an image and add a click-through URL.");
      return;
    }
    if (!validateSchedule(newAd)) return;
    await api.post("/ads", newAd);
    setNewAd(BLANK_AD);
    refresh();
    toast.success("Ad created");
  };

  const togglePauseAd = async (ad) => {
    try {
      await api.patch(`/admin/ads/${ad.ad_id}`, { active: !ad.active });
      toast.success(ad.active ? "Ad paused" : "Ad resumed");
      refresh();
    } catch { toast.error("Could not update ad"); }
  };

  const deleteAd = async (ad) => {
    if (!window.confirm(`Delete ad "${ad.title || ad.target_url}"?`)) return;
    try {
      await api.delete(`/admin/ads/${ad.ad_id}`);
      toast.success("Ad deleted");
      refresh();
    } catch { toast.error("Could not delete"); }
  };

  const saveEdit = async () => {
    if (!validateSchedule(editing)) return;
    try {
      const payload = {
        title: editing.title || "",
        target_url: editing.target_url || "",
        image_url: editing.image_url || "",
        weight: editing.weight,
        always_on: !!editing.always_on,
        flight_start: editing.always_on ? null : (editing.flight_start || null),
        flight_end: editing.always_on ? null : (editing.flight_end || null),
      };
      await api.patch(`/admin/ads/${editing.ad_id}`, payload);
      toast.success("Ad updated");
      setEditing(null);
      refresh();
    } catch { toast.error("Save failed"); }
  };

  // Compute live total weight across active in-flight ads for display
  const today = new Date().toISOString().slice(0, 10);
  const liveAds = ads.filter((a) => a.active !== false && (
    a.always_on || (!a.flight_start && !a.flight_end) ||
    ((!a.flight_start || today >= a.flight_start) && (!a.flight_end || today <= a.flight_end))
  ));
  const totalWeight = liveAds.reduce((acc, a) => acc + (parseInt(a.weight, 10) || 10), 0);

  return (
    <div>
      <RotationModeBar mode={rotationMode} onChange={saveRotationMode} />
      <ImageHelpersBar />

      <div className="card-recipe mb-6">
        <p className="overline mb-3">Create banner ad</p>
        <p className="text-sm text-rf-ink-soft mb-4">
          Upload <b>one</b> image (or a short looping video) — the live banner auto-resizes it to fit any screen. Set a weight % and a flight window (or tick &ldquo;Always on&rdquo;).
        </p>
        <div className="grid lg:grid-cols-2 gap-4 mb-4">
          <ImageInput
            value={newAd.image_url}
            onChange={(url) => setNewAd((a) => ({ ...a, image_url: url }))}
            busy={busy}
            setBusy={setBusy}
            testidPrefix="ad-new"
          />
          <div className="grid gap-3">
            <input
              data-testid="ad-target-url"
              value={newAd.target_url}
              onChange={(e) => setNewAd({ ...newAd, target_url: e.target.value })}
              placeholder="Click-through URL (https://…)"
              className="input-rf"
            />
            <input
              data-testid="ad-title"
              value={newAd.title}
              onChange={(e) => setNewAd({ ...newAd, title: e.target.value })}
              placeholder="Ad title (accessible label + text-only fallback)"
              className="input-rf"
            />
          </div>
        </div>
        <ScheduleFields ad={newAd} onChange={setNewAd} testidPrefix="ad-new" />
        <button data-testid="ad-create" onClick={createAd} className="btn-amber mt-4">Create ad</button>
      </div>

      {totalWeight > 0 && (
        <p className="text-xs text-rf-ink-soft mb-4" data-testid="ad-total-weight">
          {liveAds.length} live ad{liveAds.length === 1 ? "" : "s"} · total weight {totalWeight}. Each ad&apos;s share = its weight ÷ total weight.
        </p>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ads.map((a) => {
          const weight = parseInt(a.weight, 10) || 10;
          const live = liveAds.some((la) => la.ad_id === a.ad_id);
          const sharePct = (live && totalWeight > 0) ? Math.round((weight / totalWeight) * 100) : 0;
          return (
            <div key={a.ad_id} data-testid={`ad-card-${a.ad_id}`} className="card-recipe">
              {a.image_url ? (
                <Media src={a.image_url} alt="" className="w-full h-32 object-contain bg-rf-cream-alt rounded-lg mb-3" />
              ) : (
                <div className="w-full h-32 bg-rf-cream-alt rounded-lg mb-3 flex items-center justify-center text-rf-ink-soft text-xs">No image</div>
              )}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <p className="font-bold text-rf-ink flex-1 line-clamp-1">{a.title || "Untitled"}</p>
                {a.active === false ? (
                  <span className="chip !bg-rf-ink/10 !text-rf-ink-soft">Paused</span>
                ) : live ? (
                  <span className="chip !bg-green-100 !text-green-800">Live</span>
                ) : (
                  <span className="chip !bg-amber-100 !text-amber-800">Scheduled / ended</span>
                )}
              </div>
              <p className="text-xs text-rf-ink-soft truncate" title={a.target_url}>{a.target_url}</p>
              <div className="flex flex-wrap gap-1 mt-1.5">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-rf-amber/30 text-rf-ink font-bold">Weight {weight}{sharePct > 0 ? ` · ${sharePct}% share` : ""}</span>
                {a.always_on ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 font-bold">Always on</span>
                ) : (a.flight_start || a.flight_end) ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-rf-cream-alt text-rf-ink-soft font-bold">{a.flight_start || "—"} → {a.flight_end || "—"}</span>
                ) : null}
              </div>
              <p className="text-xs mt-2">Impressions: <span className="font-bold">{a.impressions}</span> · Clicks: <span className="font-bold">{a.clicks}</span></p>
              {(() => {
                const imp = a.impressions || 0;
                const clk = a.clicks || 0;
                const ctrPct = imp > 0 ? (clk / imp) * 100 : 0;
                const label = imp < 50
                  ? "CTR — collecting data"
                  : `CTR ${ctrPct.toFixed(ctrPct >= 10 ? 0 : 1)}%`;
                return (
                  <span
                    data-testid={`ad-ctr-${a.ad_id}`}
                    className={`inline-block mt-1.5 text-[10px] px-1.5 py-0.5 rounded font-bold ${ctrBadgeColour(ctrPct, imp)}`}
                    title={imp < 50 ? `${imp} impressions so far — need 50+ for a meaningful CTR` : `${clk} clicks ÷ ${imp} impressions`}
                  >{label}</span>
                );
              })()}
              <div className="flex gap-2 mt-3 flex-wrap">
                <button
                  data-testid={`ad-edit-${a.ad_id}`}
                  onClick={() => setEditing({ ...a, weight: parseInt(a.weight, 10) || 10 })}
                  className="btn-outline-ink !py-2 !px-3 !text-xs"
                ><PencilSimple size={14} /> Edit</button>
                <button
                  data-testid={`ad-pause-${a.ad_id}`}
                  onClick={() => togglePauseAd(a)}
                  className="btn-outline-ink !py-2 !px-3 !text-xs"
                >
                  {a.active === false ? <><Eye size={14} /> Resume</> : <><EyeSlash size={14} /> Pause</>}
                </button>
                <button
                  data-testid={`ad-delete-${a.ad_id}`}
                  onClick={() => deleteAd(a)}
                  className="text-red-600 inline-flex items-center gap-1 text-xs font-bold ml-auto"
                ><Trash size={14} /> Delete</button>
              </div>
            </div>
          );
        })}
        {ads.length === 0 && <p className="text-rf-ink-soft text-center col-span-full py-10">No ads yet — create one above.</p>}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-rf-cream border-rf-ink/10 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black text-rf-ink">Edit ad</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  data-testid="ad-edit-target-url"
                  value={editing.target_url || ""}
                  onChange={(e) => setEditing({ ...editing, target_url: e.target.value })}
                  placeholder="Click-through URL"
                  className="input-rf"
                />
                <input
                  data-testid="ad-edit-title"
                  value={editing.title || ""}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  placeholder="Ad title"
                  className="input-rf"
                />
              </div>
              <ImageInput
                value={editing.image_url}
                onChange={(url) => setEditing((a) => ({ ...a, image_url: url }))}
                busy={editBusy}
                setBusy={setEditBusy}
                testidPrefix="ad-edit"
              />
              <ScheduleFields ad={editing} onChange={setEditing} testidPrefix="ad-edit" />
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setEditing(null)} className="btn-outline-ink">Cancel</button>
                <button data-testid="ad-edit-save" onClick={saveEdit} className="btn-amber">Save changes</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
